import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useReturnPath } from '@/components/auth/authReturn';
import { AuthMark, AuthShell } from '@/components/auth/AuthShell';
import { AUTH_TEXT_LINK } from '@/components/auth/authStyles';
import { Button } from '@/components/ui/button';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/api';
import { recallLoginEmail, rememberLoginEmail } from '@/lib/loginEmailMemory';
import { cn } from '@/lib/utils';
import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
export default function ForgotPassword() {
    const { t } = useTranslation();
    const { toast } = useToast();
    // Prefilled with the email the user already typed on the login screen, so
    // following "Forgot Password?" never means retyping the address.
    const [email, setEmail] = useState(() => recallLoginEmail());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    // Back to whichever sign-in sent the user here: the Link sign-in keeps a
    // Link practice inside the Link-branded flow (/login by default).
    const backHref = useReturnPath('signIn');
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast({
                title: t('validation.error'),
                description: t('validation.emailRequired'),
                variant: 'destructive',
            });
            return;
        }
        setIsSubmitting(true);
        // Keep the (possibly corrected) address for the trip back to /login.
        rememberLoginEmail(email);
        try {
            await apiRequest('POST', '/auth/forgot-password', { email });
        }
        catch {
            // Intentionally ignore errors to prevent email enumeration
        }
        finally {
            setIsSubmitting(false);
            setIsSuccess(true);
        }
    };
    if (isSuccess) {
        return (_jsxs(AuthShell, { children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsx("div", { className: "mb-4 flex justify-center", children: _jsx(CheckCircle2, { className: "text-primary h-16 w-16" }) }), _jsx(CardTitle, { className: "text-2xl font-bold", children: t('forgotPassword.successTitle') }), _jsx(CardDescription, { children: t('forgotPassword.successDescription') })] }), _jsx(CardContent, { children: _jsx(Link, { href: backHref, children: _jsx(Button, { className: "w-full", variant: "outline", children: t('forgotPassword.backToLogin') }) }) })] }));
    }
    return (_jsxs(AuthShell, { children: [_jsxs(CardHeader, { className: "space-y-1 text-center", children: [_jsx(AuthMark, {}), _jsx(CardTitle, { className: "text-2xl font-bold", children: t('forgotPassword.title') }), _jsx(CardDescription, { children: t('forgotPassword.subtitle') })] }), _jsx(CardContent, { children: _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "email", children: t('login.email') }), _jsx(Input, { id: "email", type: "email", placeholder: t('login.emailPlaceholder'), value: email, onChange: (e) => setEmail(e.target.value), disabled: isSubmitting, autoComplete: "email", enterKeyHint: "send" })] }), _jsx(Button, { type: "submit", className: "w-full", loading: isSubmitting, loadingText: t('forgotPassword.sending'), children: t('forgotPassword.sendResetLink') }), _jsx("div", { className: "text-center", children: _jsx(Link, { href: backHref, className: cn(AUTH_TEXT_LINK, 'text-muted-foreground text-sm'), children: t('forgotPassword.backToLogin') }) })] }) })] }));
}
